import type { BufferGeometry, Group, Material, Texture } from 'three';
import { Pcg32 } from '../../../sim/rng';
import { createEmissiveMaterial, createToonMaterial, OUTLINE } from '../../materials/toon';
import type { Palette } from '../../palette';
import { footprintGeometry, MAX_FOOTPRINTS } from '../decals/footprints';
import { type Batch, createBatch } from './batch';
import {
  alertGeometry,
  blobGeometry,
  bombBodyGeometry,
  bombLampGeometry,
  cubeGeometry,
  grenadeGeometry,
  queryGeometry,
  rocketBodyGeometry,
  rocketTailGeometry,
  scorchGeometry,
  slashGeometry,
  starGeometry,
  tracerGeometry,
} from './geometry';

/** Hard cap on live particles; past it the particle nearest the end of its life is recycled. */
export const MAX_PARTICLES = 400;
export const MAX_SCORCH = 40;
const MAX_TRACERS = 96;
const MAX_THROWN = 16;
const MAX_ROCKETS = 8;
const MAX_BOMBS = 8;
const MAX_MARKERS = 64;

export interface Batches {
  cubeGround: Batch;
  cubeDirt: Batch;
  cubeMetal: Batch;
  cubeTimber: Batch;
  cubeConcrete: Batch;
  cubeBlood: Batch;
  cubeBrass: Batch;
  cubeEmber: Batch;
  dust: Batch;
  smokeDark: Batch;
  smokeMid: Batch;
  flash: Batch;
  slash: Batch;
  tracerPlayer: Batch;
  tracerEnemy: Batch;
  grenade: Batch;
  rocketBody: Batch;
  rocketTail: Batch;
  bombBody: Batch;
  bombLamp: Batch;
  scorch: Batch;
  /** ROUND5 §4: the ranger's footprints, redrawn every frame so they can be seen to fade. */
  footprint: Batch;
  barkAlert: Batch;
  barkQuery: Batch;
}

/** Every batch, by name and as the colour tables that bursts and smoke pick from. */
export interface BatchSet {
  /** All batches in creation order, for the per-frame reset/flush and for disposal. */
  all: Batch[];
  materials: Material[];
  b: Batches;
  /** Light to dark down the fire ramp; a fireball hops from one batch to the next. */
  fire: Batch[];
  smoke: Batch[];
  impactDebris: Batch[];
  blastDebris: Batch[];
  wreckDebris: Batch[];
  bloodDebris: Batch[];
}

/**
 * Builds the three MRT-patched materials and every batch under `root`. All colours come
 * from the palette; the emissive material is the only self-lit one (05 §8.1: no alpha).
 */
export function createBatchSet(root: Group, p: Palette, gradientMap: Texture, emissiveBoost: number): BatchSet {
  // Particles never take an outline (class 0, 05 §8); the few solid objects read better with one.
  const lit = createToonMaterial({ gradientMap, outline: OUTLINE.none });
  const solid = createToonMaterial({ gradientMap, outline: OUTLINE.prop });
  const glow = createEmissiveMaterial(gradientMap, emissiveBoost);

  const all: Batch[] = [];
  const make = (geometry: BufferGeometry, material: Material, capacity: number, castShadow = false, persistent = false): Batch => {
    const batch = createBatch(root, geometry, material, capacity, castShadow, material !== glow, persistent);
    all.push(batch);
    return batch;
  };
  // Lump shapes come from their own fixed stream, so they are the same every run.
  const shapes = new Pcg32(1987, 3);
  const cubes = (ref: string, material: Material = lit): Batch => make(cubeGeometry(p.color(ref)), material, MAX_PARTICLES);
  const blobs = (ref: string, top: string, material: Material, squash: number, jitter: number): Batch =>
    make(blobGeometry(p.color(ref), p.color(top), shapes, squash, jitter), material, MAX_PARTICLES);

  const snowy = p.biome === 'arctic';
  const core = {
    cubeGround: cubes('ground.mid'),
    cubeDirt: cubes('dirt.mid'),
    cubeMetal: cubes('metal.lo'),
    cubeTimber: cubes('timber.mid'),
    cubeConcrete: cubes('concrete.mid'),
    cubeBlood: cubes('blood.0'),
    cubeBrass: cubes('fire.2'),
    cubeEmber: cubes('fire.1', glow),
    dust: blobs(snowy ? 'ground.2' : 'dirt.hi', snowy ? 'ground.3' : 'dirt.hi', lit, 0.8, 0.16),
    smokeDark: blobs('ink.1', 'metal.lo', lit, 0.85, 0.18),
    smokeMid: blobs('metal.lo', 'metal.mid', lit, 0.85, 0.18),
    flash: make(starGeometry(p), glow, MAX_PARTICLES),
    slash: make(slashGeometry(p), glow, MAX_PARTICLES),
    tracerPlayer: make(tracerGeometry(p.color('playerAccent.0'), p.color('playerAccent.0')), glow, MAX_TRACERS),
    // Red on top, where the camera looks: daylight adds to emission, and orange pushed
    // brighter would quantise to the player's amber.
    tracerEnemy: make(tracerGeometry(p.color('fire.1'), p.color('fire.0')), glow, MAX_TRACERS),
    grenade: make(grenadeGeometry(p), solid, MAX_THROWN, true),
    rocketBody: make(rocketBodyGeometry(p), solid, MAX_ROCKETS, true),
    rocketTail: make(rocketTailGeometry(p), glow, MAX_ROCKETS),
    bombBody: make(bombBodyGeometry(p), solid, MAX_BOMBS, true),
    bombLamp: make(bombLampGeometry(p), glow, MAX_BOMBS),
    scorch: make(scorchGeometry(p, shapes), lit, MAX_SCORCH, false, true),
    // Not persistent: unlike a scorch mark a print fades, so it is rebuilt from the world each frame.
    footprint: make(footprintGeometry(p), lit, MAX_FOOTPRINTS),
  };
  const fire = [
    blobs('fire.3', 'fire.3', glow, 0.8, 0.12),
    blobs('fire.2', 'fire.3', glow, 0.8, 0.12),
    blobs('fire.1', 'fire.2', glow, 0.8, 0.12),
    blobs('fire.0', 'fire.1', glow, 0.8, 0.12),
  ];
  const b: Batches = {
    ...core,
    barkAlert: make(alertGeometry(p), glow, MAX_MARKERS),
    barkQuery: make(queryGeometry(p), glow, MAX_MARKERS),
  };

  return {
    all,
    materials: [lit, solid, glow],
    b,
    fire,
    smoke: [b.smokeDark, b.smokeMid],
    impactDebris: [b.cubeGround, b.cubeDirt],
    blastDebris: [b.cubeGround, b.cubeDirt, b.cubeDirt, b.cubeMetal, b.cubeEmber],
    wreckDebris: [b.cubeTimber, b.cubeTimber, b.cubeMetal, b.cubeConcrete, b.cubeEmber],
    bloodDebris: [b.cubeBlood],
  };
}
